/**
 * Custom hooks for partners content sourced from local YAML files.
 *
 * Content files:
 * - content/partners/*.yaml - Individual partner profiles
 *
 * Partner categories:
 * - professional-services: Professional Services
 * - database: Database Vendors
 * - system-integrator: System Integrators
 * - observability: Observability
 * - semantic-layer: Semantic Layers
 * - cloud-provider: Cloud Providers
 * - etl: ETL Services
 * - data-catalog: Data Catalogs
 * - community: Community
 *
 * To update content, edit the YAML files directly. Changes take effect on rebuild.
 */

import { useStaticQuery, graphql } from "gatsby"

/**
 * Partner category definitions with display labels
 */
export const PARTNER_CATEGORIES = {
  "professional-services": {
    id: "professional-services",
    label: "Professional Services",
    order: 1,
  },
  database: {
    id: "database",
    label: "Database Vendors",
    order: 2,
  },
  "system-integrator": {
    id: "system-integrator",
    label: "System Integrators",
    order: 3,
  },
  observability: {
    id: "observability",
    label: "Observability",
    order: 4,
  },
  "semantic-layer": {
    id: "semantic-layer",
    label: "Semantic Layers",
    order: 5,
  },
  "cloud-provider": {
    id: "cloud-provider",
    label: "Cloud Providers",
    order: 6,
  },
  etl: {
    id: "etl",
    label: "ETL Services",
    order: 7,
  },
  "data-catalog": {
    id: "data-catalog",
    label: "Data Catalogs",
    order: 8,
  },
  community: {
    id: "community",
    label: "Community",
    order: 9,
  },
}

/**
 * Internal hook that fetches all partners data from local YAML files.
 */
function usePartnersData() {
  return useStaticQuery(graphql`
    query PartnersContentQuery {
      allPartnersYaml(sort: { order: ASC }) {
        nodes {
          name
          slug
          logo
          website
          partnerPage
          categories
          description
          featured
          order
        }
      }
    }
  `)
}

/**
 * Get all partner profiles.
 *
 * @returns {Array} Array of partner objects
 */
export function useAllPartners() {
  const data = usePartnersData()
  return data.allPartnersYaml?.nodes || []
}

/**
 * Get partners grouped by category.
 * Partners with multiple categories will appear in each group.
 *
 * @returns {Object} Partners grouped by category ID
 */
function usePartnersByCategory() {
  const partners = useAllPartners()
  const grouped = {}

  partners.forEach((partner) => {
    const categories = partner.categories || ["professional-services"]
    categories.forEach((category) => {
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(partner)
    })
  })

  return grouped
}

/**
 * Get category labels for a list of category IDs.
 *
 * @param {Array<string>} categoryIds - Array of category IDs
 * @returns {Array<Object>} Array of category objects with id and label
 */
export function getCategoryLabels(categoryIds) {
  if (!categoryIds) return []
  return categoryIds
    .map((id) => PARTNER_CATEGORIES[id])
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
}

/**
 * Get categories that have at least one partner.
 *
 * @returns {Array} Array of category objects with partners
 */
export function useActivePartnerCategories() {
  const partnersByCategory = usePartnersByCategory()

  return Object.entries(PARTNER_CATEGORIES)
    .filter(([id]) => partnersByCategory[id]?.length > 0)
    .map(([id, category]) => ({
      ...category,
      partners: partnersByCategory[id] || [],
    }))
    .sort((a, b) => a.order - b.order)
}
